import { Box, Stack } from "@mui/material";
import { Ref, forwardRef, useEffect, useImperativeHandle} from "react";
import { useTranslation } from "react-i18next";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import { BaseActions, BaseActionEnum } from "../../../components/action-menu/ActionMenuItem";
import { ContentContainer } from "../../../components/content-container/ContentContainer";
import { ContentSection } from "../../../components/content-section/ContentSection";
import { ViewOptionStateEnum } from "../../../components/contextual-app-bar/ViewOptionStateEnum";
import LoadingZone from "../../../components/loading-zone/LoadingZone";
import NoData from "../../../components/no-data/NoData";
import OptionsSideSheet from "../../../components/options-side-sheet/OptionsSideSheet";
import SideSheetFilterInput from "../../../components/side-sheet/side-sheet-filter-input/SideSheetFilterInput";
import VirtualizedList from "../../../components/virtualized-list/VirtualizedList";
import { AlarmScheduleListActionEnum, AlarmScheduleItem } from "./AlarmScheduleListUtils";
import { useOrganizations } from "../../../queries/organizations/UseOrganizations";
import { useAlarmSchedules } from "../../../queries/alarm-schedule-admin/UseAlarmSchedules";
import useAlarmScheduleListState from "./AlarmScheduleListState";
import { AlarmScheduleListItem } from "./AlarmScheduleListItem";
import AlarmScheduleEditorDialog, { bindAlarmScheduleEditorDialog, useAlarmScheduleEditorDialogState } from "../alarm-schedule-editor/AlarmScheduleEditorDialog";
import { useDeleteAlarmSchedule } from "../../../queries/alarm-schedule-admin/UseDeleteAlarmSchedule";
import { useConfirmation } from "../../../contexts/confirmation-context/ConfirmationContext";
import { IAlarmScheduleLinkedAlarmSchemeModel } from "../../../services/alarm-schedule-data-service/Models";
import AlarmSchemeEditorDialog, { bindAlarmSchemeEditorDialog, useAlarmSchemeEditorDialogState } from "../../../components/alarm-scheme-editor/AlarmSchemeEditorDialog";
import { publish } from "pubsub-js";
import { OBJECT_CHANGED_TOPIC, ObjectChangedMsg } from "../../../common/models/pub-sub/PubSubTopics";
import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";
import { contains } from "../../../common/utilities/Contains";

export type AlarmScheduleListType = {
    executeAction: (action: AlarmScheduleListActionEnum | BaseActions) => void;
};

export type AlarmScheduleListProps = {
    organizationID: number;
    groupID: number;
    setViewOptionState?: (state: ViewOptionStateEnum) => void;
};

function AlarmScheduleList({
    organizationID,
    groupID,
    setViewOptionState,
}: AlarmScheduleListProps, ref: Ref<unknown> | undefined) {

    const { t } = useTranslation();

    const { confirm
        , acknowledge 
    } = useConfirmation();


    const alarmScheduleEditorDialog = useAlarmScheduleEditorDialogState();
    const alarmSchemeEditorDialog = useAlarmSchemeEditorDialogState();
    
    const {
        state,
        setFilterText,
        resetState,
    } = useAlarmScheduleListState();

    const { data: organizations } = useQueryNotifications(useOrganizations)({});
    const organization = organizations?.find(o => o.id === organizationID);

    const { data, status, refetch } = useQueryNotifications(useAlarmSchedules)({
        groupID: organization?.peerGroupID ?? -1,
        organizationID: organizationID,
        includeLinkedAlarmSchemes: true,
        enabled: organization !== undefined
    });

    const { mutate: deleteAlarmSchedule } = useQueryNotifications(useDeleteAlarmSchedule)({
        organizationID: organizationID,        
        groupID: organization?.peerGroupID ?? -1,
        onSuccess: () => {
            acknowledge(t("alarmSchedule.deleted"));
        }
    });

    const nodata = status === "success" && (data?.length ?? 0) === 0; 

    useImperativeHandle<unknown, AlarmScheduleListType>(
        ref,
        () => ({
             executeAction: executeAction
        })
    );

    async function _editAlarmSchedule(id: number) {

        const result = await alarmScheduleEditorDialog.open({
            organizationID: organizationID,
            groupID: organization?.peerGroupID as number,
            alarmScheduleID: id,
        });

        if (result.reason === 'save') {
            refetch();
        }
    }

    async function onAddAlarmSchedule() {
        _editAlarmSchedule(-1);
    }

    async function onEditAlarmSchedule(item: AlarmScheduleItem) {
        _editAlarmSchedule(item.id as number);
    }

    async function onDeleteAlarmSchedule(item: AlarmScheduleItem) {

        const result = await confirm({
            dialogTitle: t("deleteConfirmation.title1", { itemName: item.name }),
            confirmationText: t("deleteConfirmation.confirmationText"),
            yesButtonText: t("deleteConfirmation.yesButtonText"),
            noButtonText: t("deleteConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: false
        });

        if (result === 'yes') {

            deleteAlarmSchedule(item.id);
        }
    }

    async function onEditAlarmScheme(item: IAlarmScheduleLinkedAlarmSchemeModel) {
        const result = await alarmSchemeEditorDialog.open({
            groupID,
            organizationID,
            alarmSchemeID: item.id
        });

        if (result.reason === 'save') {
            await refetch();

            setTimeout(() => {
                publish(OBJECT_CHANGED_TOPIC, {
                    entity: {
                        id: item.id,
                        objectType: eLynxObjectTypeEnum.AlarmScheme
                    },
                    name: item.name
                } as ObjectChangedMsg);
            }, 500);

        }
    }


    function onAlarmScheduleAction(action: AlarmScheduleListActionEnum, 
        payload: AlarmScheduleItem | IAlarmScheduleLinkedAlarmSchemeModel) {

        switch (action) {

            case AlarmScheduleListActionEnum.Edit:
                onEditAlarmSchedule(payload as AlarmScheduleItem);
                break;


            case AlarmScheduleListActionEnum.Delete:
                 onDeleteAlarmSchedule(payload as AlarmScheduleItem);
                 break;

            case AlarmScheduleListActionEnum.EditAlarmScheme:
                onEditAlarmScheme(payload as IAlarmScheduleLinkedAlarmSchemeModel);
                break;
        }
    }

    function renderItem(_index: number, item: AlarmScheduleItem) {
        return (
            <AlarmScheduleListItem
                key={`${item.id}`}
                item={item}
                onAction={onAlarmScheduleAction}
            />
        );
    }

    function executeAction(action: AlarmScheduleListActionEnum | BaseActionEnum) {

        switch (action) {
            case AlarmScheduleListActionEnum.Add:
                onAddAlarmSchedule();
                break;
        }
     }

     function onFilterItem(item: AlarmScheduleItem): boolean {

        return !!contains(item.name, state.filterText)
    
    }


     useEffect(() => {
        setViewOptionState && setViewOptionState(state.viewOptionState);
    }, [
        state.viewOptionState,
        setViewOptionState
    ]);

    return (
        <LoadingZone isLoading={status === 'loading'}>

            <ContentContainer>
                <ContentSection sx={{
                    padding: nodata ? 1 : undefined
                }}>
                    {nodata ? 
                        <NoData
                            title={t("alarmSchedule.nodata.title")}
                            text={<Stack
                                direction="row"
                                spacing={0.5}
                                sx={{
                                    alignItems: "flex-end"
                                }}>
                                <Box>
                                    {t("alarmSchedule.nodata.text")}
                                </Box>
                            </Stack>
                            }
                        /> :
                        null
                    }
                
                    <VirtualizedList
                        items={data ?? []} 
                        renderItem={renderItem}
                        filter={onFilterItem}
                    />
                
                </ContentSection>

                <OptionsSideSheet onReset={resetState}>
                    
                    <SideSheetFilterInput
                        label={t("filterBy")}
                        value={state.filterText}
                        onChange={(value: string) => {
                            setFilterText(value);
                        }}
                    />

                </OptionsSideSheet>
                
            </ContentContainer>

             <AlarmScheduleEditorDialog {...bindAlarmScheduleEditorDialog(alarmScheduleEditorDialog)} />
             <AlarmSchemeEditorDialog {...bindAlarmSchemeEditorDialog(alarmSchemeEditorDialog)} />

        </LoadingZone>
    );
}

export default forwardRef(AlarmScheduleList);